import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { STATIC } from '../../lib/queryPresets';
import {
  createExpense,
  deleteExpense,
  listExpenses,
  setDossierDeboursMandate,
  updateExpense,
  uploadExpenseReceipt,
  type DossierExpense,
  type ExpenseInput,
} from '../../services/billing/expenseService';
import { dossiersKeys } from './useDossiers';

export const expenseKeys = {
  all: ['expenses'] as const,
  list: (dossierId?: string) => [...expenseKeys.all, 'list', dossierId ?? 'all'] as const,
};

export function useExpenses(dossierId?: string) {
  return useQuery({
    ...STATIC,
    queryKey: expenseKeys.list(dossierId),
    queryFn: () => listExpenses({ dossierId }),
  });
}

export function useSaveExpense() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ id, input }: { id?: string; input: ExpenseInput }) => (id ? updateExpense(id, input) : createExpense(input)),
    onSuccess: () => qc.invalidateQueries({ queryKey: expenseKeys.all }),
  });
}

export function useDeleteExpense() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (expense: Pick<DossierExpense, 'id' | 'receipt_path'>) => deleteExpense(expense),
    onSuccess: () => qc.invalidateQueries({ queryKey: expenseKeys.all }),
  });
}

export function useUploadExpenseReceipt() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ expense, file }: { expense: Pick<DossierExpense, 'id' | 'dossier_id' | 'receipt_path'>; file: File }) =>
      uploadExpenseReceipt(expense, file),
    onSuccess: () => qc.invalidateQueries({ queryKey: expenseKeys.all }),
  });
}

export function useSetDeboursMandate() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ dossierId, on, ref }: { dossierId: string; on: string | null; ref: string | null }) =>
      setDossierDeboursMandate(dossierId, on, ref),
    onSuccess: (_d, vars) => {
      qc.invalidateQueries({ queryKey: dossiersKeys.detail(vars.dossierId) });
      qc.invalidateQueries({ queryKey: dossiersKeys.all });
    },
  });
}
